import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { areFingerprintsEqual } from '../../../shared/lib/stableFingerprint';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import { executeValidationGrpc } from '../../../shared/api/grpc/validationGrpcClient';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import {
  postValidationRun,
  generateValidationTraceId,
  type ValidationRunPayload,
} from '../../../shared/api/validationRunsClient';
import { snapshotCurrentPcfSettingsStoreForDebug } from '../../../shared/api/pipeex/pcfSettingsClient';
import { flushWebLogger, webLog } from '../../../shared/api/grpc/webLogger';
import { describeValidationContext } from '../lib/validationContextFingerprint';
import {
  buildValidationRunSignatures,
  getSupersededRunReason,
  getValidationInvalidationEpoch,
} from '../lib/validationRunCoordinator';
import type { ValidationRunCoordinator } from '../lib/validationRunCoordinator';
import {
  filterSelectableValidationIds,
  mergeValidationReportCheckResults,
  parseValidationReportJson,
} from '../lib/validationReportNormalizer';
import { applyValidationMetadata, formatMs, nowMs } from '../lib/validationStateHelpers';
import { buildValidationMappingOverridesFromStore } from '../lib/validationMappingOverrides';
import { syncValidationMappingsToHost } from '../lib/validationMappingSync';
import {
  buildBaseProgressMetrics,
  buildCacheHitProgress,
  buildCompleteProgress,
  buildErrorProgress,
  buildPreparingProgress,
  buildProcessingProgress,
  buildRunningProgress,
  buildSubmittingProgress,
  buildSyncingProgress,
  mapHostProgressToRunProgress,
  mapMappingSyncProgressToRunProgress,
} from '../lib/validationRunProgressBuilders';
import { USE_GRPC, USE_MOCK, getValidationRuntimeMode, waitForE2EValidationMock } from '../lib/validationRuntimeEnv';
import { MOCK_REPORT } from '../lib/validationMockData';
import type { PcfValidationReport, ValidationDefinition } from '../types';
import type { ValidationRuntimeSettings } from '../lib/validationRuntimeSettings';
import type { ValidationRunProgress } from '../lib/validationRunProgress';
import type { RunValidationOptions } from './useValidationState';

export interface UseValidationRunnerOptions {
  validations: ValidationDefinition[];
  selectedValidations: number[];
  validationSettings: ValidationRuntimeSettings;
  documentInfo: any;
  report: PcfValidationReport | null;
  commitReport: (
    report: PcfValidationReport,
    source: string,
    contextFingerprint?: string | null
  ) => void;
  setDocumentInfo: React.Dispatch<React.SetStateAction<any>>;
  coordinator: ValidationRunCoordinator<PcfValidationReport>;
  /** Opaque verified-setup token supplied by the caller, or `null` until verified. */
  verifiedRevision?: string | null;
}

export interface UseValidationRunnerReturn {
  loading: boolean;
  runProgress: ValidationRunProgress | null;
  setRunProgress: React.Dispatch<React.SetStateAction<ValidationRunProgress | null>>;
  handleRunValidation: (
    enabledValidationIds?: number[],
    options?: RunValidationOptions
  ) => Promise<void>;
}

export function useValidationRunner(options: UseValidationRunnerOptions): UseValidationRunnerReturn {
  const {
    validations,
    selectedValidations,
    validationSettings,
    documentInfo,
    report,
    commitReport,
    setDocumentInfo,
    coordinator,
    verifiedRevision = null,
  } = options;

  const { call } = useRevitApi();
  const snackbar = useSnackbarStore();
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const [loading, setLoading] = useState(false);
  const [runProgress, setRunProgress] = useState<ValidationRunProgress | null>(null);
  const validationSurfaceMountedRef = useRef(true);
  useEffect(() => {
    validationSurfaceMountedRef.current = true;
    return () => {
      validationSurfaceMountedRef.current = false;
    };
  }, []);
  /** Re-reads the current setup so awaited results cannot commit into a newer context. */
  const liveContextFingerprintRef = useRef<() => string>(() => '');
  liveContextFingerprintRef.current = () =>
    describeValidationContext({
      activeProjectKey: activeDocumentInfo.projectKey ?? null,
      documentName: documentInfo?.DocumentName ?? null,
      documentPath: documentInfo?.DocumentPath ?? null,
      verifiedRevision,
      selectedValidationIds: filterSelectableValidationIds(selectedValidations, validations),
      settingsSnapshot: { ...snapshotCurrentPcfSettingsStoreForDebug() },
      reconnectGapMarginMillimeters: validationSettings.reconnectGapMarginMillimeters,
      mappingOverrides: buildValidationMappingOverridesFromStore(),
    }).fingerprint;

  /** A result captured for another context must not be stored, shown, or cached. */
  const discardSupersededRun = useCallback(
    (reason: string): void => {
      webLog.warn('validation-preflight', `run-discarded: ${reason}`);
      if (!validationSurfaceMountedRef.current) {
        webLog.warn(
          'validation-preflight',
          'run-discard-notification-skipped: validation surface is no longer active'
        );
        return;
      }
      setRunProgress(null);
      snackbar.showInfo('Validation result discarded because the setup changed during the run.');
      webLog.warn('validation-preflight', 'run-discard-notified: validation surface was active');
    },
    [snackbar]
  );

  const handleRunValidation = useCallback(
    async (enabledValidationIds?: number[], options: RunValidationOptions = {}) => {
      const scopedValidationIds = filterSelectableValidationIds(
        Array.isArray(enabledValidationIds) ? enabledValidationIds : selectedValidations,
        validations
      );
      webLog.info(
        'validation-preflight',
        `run-attempt: mode=${getValidationRuntimeMode()} selectedValidations=${scopedValidationIds.length} forceFresh=${options.forceFresh === true}`
      );
      void flushWebLogger();

      // The interface-backed settings snapshot crosses this boundary as a plain record.
      const rawSettingsSnapshot = { ...snapshotCurrentPcfSettingsStoreForDebug() };
      const mappingOverrides = buildValidationMappingOverridesFromStore();
      // Focused results merge into a report whose fingerprint names the full selection.
      const { settings: validationRunSettingsSnapshot, fingerprint: contextFingerprint } =
        describeValidationContext({
          activeProjectKey: activeDocumentInfo.projectKey ?? null,
          documentName: documentInfo?.DocumentName ?? null,
          documentPath: documentInfo?.DocumentPath ?? null,
          verifiedRevision,
          selectedValidationIds: filterSelectableValidationIds(selectedValidations, validations),
          settingsSnapshot: rawSettingsSnapshot,
          reconnectGapMarginMillimeters: validationSettings.reconnectGapMarginMillimeters,
          mappingOverrides,
        });
      const { runSignature, preflightSignature } = buildValidationRunSignatures({
        mode: getValidationRuntimeMode(),
        documentName: documentInfo?.DocumentName ?? null,
        documentPath: documentInfo?.DocumentPath ?? null,
        activeProjectKey: activeDocumentInfo.projectKey ?? null,
        verifiedRevision,
        validationIds: scopedValidationIds,
        runSettings: validationRunSettingsSnapshot,
        preflightSettings: rawSettingsSnapshot,
        mappingOverrides,
      });

      const runStartedAt = Date.now();
      const baseProgressMetrics = buildBaseProgressMetrics(scopedValidationIds);

      const updateRunProgressFromHost = (progress: RevitOperationProgressSnapshot) => {
        setRunProgress(mapHostProgressToRunProgress(progress, runStartedAt, baseProgressMetrics));
      };
      const updateMappingSyncProgressFromHost = (progress: RevitOperationProgressSnapshot) => {
        setRunProgress(
          mapMappingSyncProgressToRunProgress(progress, runStartedAt, baseProgressMetrics)
        );
      };

      const cachedReport = options.forceFresh ? null : coordinator.getCachedReport(runSignature);
      if (cachedReport) {
        if (!areFingerprintsEqual(liveContextFingerprintRef.current(), contextFingerprint)) {
          // The cached entry predates the live context: drop it and run fresh
          // instead of resurfacing evidence for a setup that is no longer there.
          coordinator.invalidateAll();
          webLog.warn(
            'validation-preflight',
            'run-cache-hit discarded: the live context no longer matches the cached run context'
          );
        } else {
          const visibleCachedReport = options.mergeIntoCurrentReport
            ? mergeValidationReportCheckResults(report, cachedReport)
            : cachedReport;
          setRunProgress(buildCacheHitProgress(runStartedAt, baseProgressMetrics));
          commitReport(visibleCachedReport, 'cache-hit', contextFingerprint);
          if (!options.silentCacheHit) {
            snackbar.showSuccess('Validation reused cached report');
          }
          return;
        }
      }

      // Any invalidation after this point supersedes whatever the run brings back.
      const invalidationEpochAtStart = getValidationInvalidationEpoch();
      setRunProgress(buildPreparingProgress(scopedValidationIds.length, runStartedAt));
      setLoading(true);
      webLog.info(
        'validation-preflight',
        `run-start: mode=${getValidationRuntimeMode()} selectedValidations=${scopedValidationIds.length}`
      );
      void flushWebLogger();

      const runValidation = async (): Promise<PcfValidationReport | null> => {
        if (USE_MOCK) {
          return waitForE2EValidationMock(applyValidationMetadata(MOCK_REPORT, {
            lastValidationDate: MOCK_REPORT.ValidationDate,
          }));
        }

        if (coordinator.shouldSyncPreflight(preflightSignature)) {
          setRunProgress(
            buildSyncingProgress(
              'Synchronizing validation mappings',
              'Checking current PCF parameter bindings before the validation request is sent to Revit.',
              runStartedAt
            )
          );
          webLog.info(
            'validation-preflight',
            'settings-profile-sync: skipped remote profile save; validation runtime uses host mapping sync'
          );
          void flushWebLogger();
          const syncResult = await syncValidationMappingsToHost(call, mappingOverrides, {
            onProgress: updateMappingSyncProgressFromHost,
          });
          if (!syncResult.success) {
            setRunProgress(
              buildErrorProgress(
                'Validation mapping sync failed',
                syncResult.error || 'The mapped parameter bindings could not be synchronized.',
                runStartedAt,
                baseProgressMetrics
              )
            );
            snackbar.showError(
              `Failed to sync mapped parameters before validation: ${syncResult.error}`
            );
            return null;
          }
          webLog.info(
            'validation-preflight',
            `${getValidationRuntimeMode()}-run: mapped parameters synced to validation runtime before validation`
          );
          void flushWebLogger();

          coordinator.markPreflightSynced(preflightSignature);
        } else {
          setRunProgress(
            buildSyncingProgress(
              'Validation mappings already synchronized',
              'The current mapping signature already matches the host runtime for this document.',
              runStartedAt
            )
          );
        }

        const config = {
          enabledValidations: scopedValidationIds,
          validateAllElements: true,
          stopOnFirstError: false,
          includeInfoMessages: true,
          includeWarnings: true,
          forceFresh: options.forceFresh === true,
          reconnectGapMarginMillimeters: validationSettings.reconnectGapMarginMillimeters,
        };

        setRunProgress(buildSubmittingProgress(scopedValidationIds.length, runStartedAt));

        if (USE_GRPC) {
          webLog.info(
            'validation-preflight',
            `grpc-run: sending ExecuteValidation request validationIds=${scopedValidationIds.join(',')}`
          );
          void flushWebLogger();
          setRunProgress(
            buildRunningProgress(
              'Running validation in Revit',
              'Revit is collecting the model snapshot and executing the selected checks. This is the long step.',
              runStartedAt,
              [...baseProgressMetrics, { label: 'request path', value: 'ExecuteValidation' }]
            )
          );

          // F-A FR-A5 — generate a W3C trace_id used by the audit POST.
          // Connect owns transport trace injection; this id ties the audit row to it.
          const auditTraceId = generateValidationTraceId();
          const grpcStartedAt = new Date();
          const grpcStartedMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
          const grpcResult = await executeValidationGrpc(JSON.stringify(config), {
            onProgress: updateRunProgressFromHost,
          });
          const grpcDurationMs = Math.round(
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - grpcStartedMs
          );
          const grpcFinishedAt = new Date();

          if (!grpcResult.success || !grpcResult.reportJson) {
            // Audit the FAILED outcome too — dashboards key off
            // `statusText='failed'` to surface the validation-error rate.
            // Fire-and-forget; never throw from the UI path.
            void postValidationRun({
              traceId: auditTraceId,
              operation: 'validation.execute',
              statusText: 'failed',
              startedAtUtc: grpcStartedAt.toISOString(),
              finishedAtUtc: grpcFinishedAt.toISOString(),
              durationMs: grpcDurationMs,
              elementCount: 0,
              ruleCount: scopedValidationIds.length,
              errorCount: 0,
              warningCount: 0,
              errorStage: 'grpc',
              errorMessage: grpcResult.error ?? 'No report returned',
            });
            setRunProgress(
              buildErrorProgress(
                'Validation failed in Revit',
                grpcResult.error || 'The gRPC validation runtime did not return a report.',
                runStartedAt,
                baseProgressMetrics
              )
            );
            snackbar.showError(grpcResult.error || 'Validation failed via gRPC');
            return null;
          }

          setRunProgress(
            buildProcessingProgress(
              'Processing validation report',
              'The Revit result is back. Normalizing checks, issues, and dashboard data for the repair console.',
              runStartedAt
            )
          );
          const parsedReport = parseValidationReportJson(grpcResult.reportJson, 'grpc-run');
          webLog.info(
            'validation-preflight',
            `grpc-run: received ExecuteValidation report checks=${parsedReport.TotalChecks ?? 0} issues=${parsedReport.TotalIssues ?? 0}`
          );

          // F-A FR-A5 — audit POST for the SUCCESSFUL outcome. Sources
          // counters from the parsed report rather than the raw JSON so
          // we mirror exactly what the user sees on the dashboard.
          const auditPayload: ValidationRunPayload = {
            traceId: auditTraceId,
            operation: 'validation.execute',
            statusText: 'success',
            startedAtUtc: grpcStartedAt.toISOString(),
            finishedAtUtc: grpcFinishedAt.toISOString(),
            durationMs: grpcDurationMs,
            elementCount: 0,
            ruleCount: parsedReport.TotalChecks ?? scopedValidationIds.length,
            errorCount:
              (parsedReport.ErrorIssues ?? 0) + (parsedReport.CriticalIssues ?? 0),
            warningCount: parsedReport.WarningIssues ?? 0,
            modelName: parsedReport.DocumentName,
          };
          void postValidationRun(auditPayload);

          return applyValidationMetadata(parsedReport, {
            lastValidationDate: parsedReport.ValidationDate,
          });
        }

        const result = await call('validation.executeValidation', {
          config: JSON.stringify(config),
          onProgress: updateRunProgressFromHost,
        });

        if (!result.success || !result.report) {
          setRunProgress(
            buildErrorProgress(
              'Validation failed in Revit',
              result.error || 'The host validation runtime did not return a report.',
              runStartedAt,
              baseProgressMetrics
            )
          );
          snackbar.showError(result.error || 'Validation failed');
          return null;
        }

        setRunProgress(
          buildProcessingProgress(
            'Processing validation report',
            'The Revit result is back. Normalizing checks, issues, and dashboard data for the repair console.',
            runStartedAt
          )
        );
        return applyValidationMetadata(
          result.report as PcfValidationReport,
          result as any
        );
      };

      try {
        const resultReport = await coordinator.run(
          runSignature,
          Boolean(options.forceFresh),
          runValidation
        );
        if (!resultReport) {
          return;
        }

        // Recheck after the await so superseded evidence cannot be stored or surfaced.
        const supersededReason = getSupersededRunReason({
          invalidationEpochAtStart,
          runContextFingerprint: contextFingerprint,
          liveContextFingerprint: liveContextFingerprintRef.current,
        });
        if (supersededReason) {
          discardSupersededRun(supersededReason);
          return;
        }

        setRunProgress(
          buildCompleteProgress(
            resultReport.TotalChecks ?? scopedValidationIds.length,
            resultReport.TotalIssues ?? 0,
            runStartedAt,
            baseProgressMetrics
          )
        );
        const cacheStartedAt = nowMs();
        coordinator.storeReport(runSignature, resultReport);
        webLog.info(
          'validation-performance',
          `run-cache-store: elapsedMs=${formatMs(nowMs() - cacheStartedAt)} checks=${resultReport.TotalChecks ?? 0} issues=${resultReport.TotalIssues ?? 0}`
        );
        const visibleReport = options.mergeIntoCurrentReport
          ? mergeValidationReportCheckResults(report, resultReport)
          : resultReport;
        commitReport(visibleReport, 'run-complete', contextFingerprint);
        if (resultReport.DocumentName) {
          setDocumentInfo((current: any) => current ?? { DocumentName: resultReport.DocumentName });
        }

        if (USE_MOCK) {
          snackbar.showSuccess(
            `Validation completed: ${resultReport.PassedChecks}/${resultReport.TotalChecks} checks passed (MOCK)`
          );
        } else if (USE_GRPC) {
          snackbar.showSuccess('Validation completed via gRPC');
        } else {
          snackbar.showSuccess(
            `Validation completed: ${resultReport.PassedChecks}/${resultReport.TotalChecks} checks passed`
          );
        }
      } catch (err: any) {
        setRunProgress(
          buildErrorProgress(
            'Validation run stopped',
            err.Message || err.message || 'Validation error',
            runStartedAt,
            baseProgressMetrics
          )
        );
        snackbar.showError(err.Message || err.message || 'Validation error');
      } finally {
        setLoading(false);
      }
    },
    [
      call,
      activeDocumentInfo.projectKey,
      commitReport,
      coordinator,
      discardSupersededRun,
      documentInfo?.DocumentName,
      documentInfo?.DocumentPath,
      report,
      selectedValidations,
      setDocumentInfo,
      snackbar,
      validationSettings.reconnectGapMarginMillimeters,
      validations,
      verifiedRevision,
    ]
  );

  return {
    loading,
    runProgress,
    setRunProgress,
    handleRunValidation,
  };
}
